import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/Button';

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
      <p className="text-[11px] font-semibold text-[#706E6B]">{label}</p>
      <p className="text-[22px] font-bold text-[#2E2E2E] mt-1">{value}</p>
    </div>
  );
}

export default function GoogleAdsAnalyticsPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/marketing/google-ads/analytics/overview');
      setOverview(data);
    } catch (err) {
      alertService.error('Failed to load analytics', err.response?.data?.error || 'Select a Google Ads account under Settings first.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post('/marketing/google-ads/analytics/sync-all');
      alertService.toast('success', 'Synced latest Google Ads data');
      load();
    } catch (err) {
      alertService.error('Sync failed', err.response?.data?.error || 'Could not sync.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Loading…</div>;

  if (!overview?.connected) {
    return <div className="text-center py-16 text-[13px] text-[#5C5C5C]">Connect Google Ads and select an account under Settings to see analytics.</div>;
  }

  const totals = overview.last30Days || {};

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button variant="outline" onClick={sync} loading={syncing}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Sync Now
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Spend (30d)" value={`${(totals.spend || 0).toFixed(2)}`} />
        <StatCard label="Impressions (30d)" value={(totals.impressions || 0).toLocaleString()} />
        <StatCard label="Clicks (30d)" value={(totals.clicks || 0).toLocaleString()} />
        <StatCard label="CTR" value={`${((overview.ctr || 0) * 100).toFixed(2)}%`} />
      </div>
    </div>
  );
}
